import React, { useContext, useEffect, useState } from "react";
import { giftCardData, companyConfig } from "../myGiftCardList";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useDispatch } from "react-redux";
import { listAllGiftCards } from "../../../reducers/giftcard.slice";
import {
  downloadBarcodeRedemption,
  listAllGiftCardRedemption,
} from "../../../reducers/payouts.slice";
import {
  convertDateFormatInUtc,
  downloadDocument,
  formatNumberToDollar,
} from "../../../utils/util";
import { AuthContext } from "../../../context/authContext";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import useMediaQuery from "../../../components/shared/useMediaQuery";

/* ── card tile helpers ─────────────────────────────────────── */
const cardLogoWrapperStyle = (bg) => ({
  background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)",
  backgroundColor: bg || "#000000",
  borderRadius: 4,
  height: 200,
  position: "relative",
});

const infoLayoutStyle = (bg) => ({
  height: 30,
  padding: "4px 8px",
  boxShadow: "3px 1px 20px 1px rgba(0,0,0,0.27)",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: bg
    ? `linear-gradient(to right, rgba(255,255,255,0), #ff00007d 30%, #ff0000b8 50%, rgba(255,255,255,0))`
    : `linear-gradient(to right, rgba(255,255,255,0.2), rgba(255,255,255,0.4), rgba(255,255,255,0.2))`,
});

const purchasedInfoStyle = {
  background: "#000000B8",
  boxShadow: "3px 1px 20px 1px rgba(0,0,0,0.27)",
  textAlign: "center",
  height: 30,
  padding: "4px 8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const infoTextStyle = {
  fontSize: "0.8rem",
  letterSpacing: 1,
  textTransform: "uppercase",
  color: "#FFFFFFB8",
  fontWeight: 600,
  fontFamily: "Arial",
};

const cardLogoFrameStyle = {
  height: 150,
  padding: "48px 59px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const cardDetailsStyle = {
  height: 128,
  padding: "6px 16px 24px 16px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const cardDescStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  marginTop: 10,
};

/* ── GiftCards ─────────────────────────────────────────────── */
const GiftCards = () => {
  const dispatch = useDispatch();
  const [selectedCard, setSelectedCard] = useState(null);
  const [openStoreCard, setOpenStoreCard] = useState(false);
  const [openCardProcessing, setOpenCardProcessing] = useState(false);
  const [openOrderSuccess, setOpenOrderSuccess] = useState(false);
  const [openRejectedCard, setOpenRejectedCard] = useState(false);
  const [transformedData, setTransformedData] = useState({});
  const [imageBase64, setImageBase64] = useState("");

  const { showToast } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showLoader, setShowLoader] = useState(false);
  const [storeCardData, setStoreCardData] = useState([]);

  const isXL = useMediaQuery("(min-width: 1536px)");
  const isMedium = useMediaQuery("(max-width: 900px)");

  const getAllCards = () => {
    setShowLoader(true);
    dispatch(listAllGiftCards()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }

      const filteredGiftCards = Object.entries(res?.payload).reduce(
        (acc, [retailer, cards]) => {
          const activeCards = cards.filter((card) => card.status !== "INACTIVE");
          if (activeCards.length > 0) acc[retailer] = activeCards;
          return acc;
        },
        {}
      );

      const vendorsWithSummary = Object.entries(filteredGiftCards)?.reduce(
        (acc, [retailer, cards]) => {
          const amounts = cards?.map((card) => card?.amount);
          const boosts = cards?.map((card) => card?.boost);
          let hasInStore = false;
          let hasOnline = false;
          let hasBoth = false;
          for (const card of cards) {
            const types = card?.gift_card_type;
            if (types.includes("IN_STORE") && types.includes("ONLINE")) { hasBoth = true; break; }
            if (types.includes("IN_STORE")) hasInStore = true;
            if (types.includes("ONLINE")) hasOnline = true;
          }
          let summarized_type = [];
          if (hasBoth) summarized_type = ["IN_STORE", "ONLINE"];
          else if (hasOnline) summarized_type = ["ONLINE"];
          else if (hasInStore) summarized_type = ["IN_STORE"];
          acc[retailer] = {
            gift_cards: cards,
            min_amount: Math.min(...amounts),
            max_amount: Math.max(...amounts),
            max_boost: Math.max(...boosts),
            gift_card_type: summarized_type,
          };
          return acc;
        },
        {}
      );

      setTransformedData(vendorsWithSummary);
      setShowLoader(false);
    });
  };

  useEffect(() => { getAllCards(); }, []);

  useEffect(() => {
    setShowLoader(true);
    dispatch(listAllGiftCardRedemption()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      const formattedGiftCard = res?.payload?.map((item) => ({
        ...item,
        created_at: dayjs(item?.created_at).format("MM/DD/YYYY"),
      }));
      setStoreCardData(formattedGiftCard);
      setShowLoader(false);
    });
  }, []);

  const handleStoreCardOpen = async (card) => {
    const enrichedGiftCard = {
      ...card,
      ...(companyConfig[card?.gift_card_details?.retailer] || {
        logo: "/logos/default.png",
        backgroundColor: "#ccc",
      }),
    };

    if (card?.status === "APPROVED" || card?.status === "PENDING_APPROVAL") {
      setSelectedCard(enrichedGiftCard);
      setOpenCardProcessing(true);
    } else if (card?.status === "REJECTED") {
      setSelectedCard(enrichedGiftCard);
      setOpenRejectedCard(true);
    } else {
      const response = await dispatch(downloadBarcodeRedemption({ id: card?.id }));
      if (response?.error && Object.keys(response?.error)?.length > 0) {
        showToast(response?.error?.message, "error");
        return;
      }
      setImageBase64(
        response?.payload?.length === 0
          ? ""
          : `data:${response?.payload[0]?.content_type};base64,${response?.payload[0]?.document_bytes}`
      );
      setSelectedCard(enrichedGiftCard);
      setOpenStoreCard(true);
    }
  };

  const cardTypeLabel = (types) =>
    types?.length === 1
      ? types[0]
      : types?.join(" & ")?.replace(/_/g, " ");

  const retailerLabel = (r) =>
    r?.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

  /* ── card tile ───────────────────────────────────────────── */
  const CardTile = ({ bg, logo, infoSlot, typeLabel, companyLabel, priceLabel, onClick }) => (
    <div
      onClick={onClick}
      style={{ padding: 8, borderRadius: 10, border: "1px solid #3C3C3C", cursor: "pointer" }}
    >
      <div style={{ width: "100%", height: 350, borderRadius: 10, display: "flex", flexDirection: "column" }}>
        <div style={cardLogoWrapperStyle(bg)}>
          {infoSlot}
          <div style={cardLogoFrameStyle}>
            <div>
              <img src={logo} alt={companyLabel} style={{ width: 125, height: 40, objectFit: "contain" }} />
            </div>
          </div>
        </div>
        <div style={cardDetailsStyle}>
          <div style={cardDescStyle}>
            <span style={{ fontWeight: 400, fontSize: "0.85rem", lineHeight: "100%", letterSpacing: 2, textTransform: "uppercase" }}>
              {typeLabel}
            </span>
            <span style={{ fontWeight: 700, fontSize: "1.3rem", letterSpacing: 1, display: "block" }}>
              {companyLabel}
            </span>
          </div>
          <div style={{ height: 24, marginTop: 5 }}>
            <p style={{ height: 23, fontWeight: 400, fontSize: "1.7rem", letterSpacing: 0, color: "hsl(0 0% 70% / 1)", margin: 0 }}>
              {priceLabel}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── dialog card preview ─────────────────────────────────── */
  const DialogCardPreview = ({ card, infoSlot, showBarcodeSection }) => (
    <div style={{ border: "1px solid #3C3C3C", borderRadius: 10, padding: 8 }}>
      <div>
        <div
          style={{
            background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)",
            backgroundColor: card?.bg,
            borderRadius: 4,
          }}
        >
          {infoSlot}
          <div>
            <div style={{ textAlign: "center", paddingTop: 30, height: 100 }}>
              <img src={card?.logo} alt={card?.retailer} style={{ height: 32 }} />
            </div>
            {showBarcodeSection && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", justifyContent: "center", padding: "8px 0" }}>
                <span style={{ fontSize: "1.3rem", letterSpacing: 2 }}>{card?.gift_card_code}</span>
                <span style={{ fontSize: "1.1rem", letterSpacing: 2 }}>PIN: {card?.gift_card_pin}</span>
                {imageBase64 && (
                  <div style={{
                    height: 100, width: 280, margin: "20px auto",
                    backgroundImage: `url(${imageBase64})`,
                    backgroundPosition: "center",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    borderRadius: 4,
                  }} />
                )}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", padding: "0 30px", height: 84 }}>
              <span style={{ fontWeight: 400, fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>
                {cardTypeLabel(card?.gift_card_details?.gift_card_type)}
              </span>
              <span style={{ fontSize: "1.2rem", letterSpacing: 2, fontWeight: 700 }}>
                ${card?.discounted_amount}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ margin: "40px 0" }}>
      {/* My Gift Cards section */}
      <div style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: "1.4rem", fontWeight: 700, margin: "0 0 4px 0" }}>My Gift Cards</h2>
          <p style={{ color: "rgba(255,255,255,0.52)", fontFamily: "Inter", fontWeight: 300, fontSize: 15, margin: 0 }}>
            Keep track of all your Gift Card purachases here.
          </p>
        </div>
        {storeCardData?.length >= 4 && (
          <Link to="my-gift-cards" style={{ fontWeight: 400, fontSize: 14, color: "#2196F3", textDecoration: "none" }}>
            View All
          </Link>
        )}
      </div>
      <br />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
        {storeCardData
          ?.slice(0, isXL ? 4 : isMedium ? 2 : 3)
          ?.map((card, index) => {
            const { logo, bg } = companyConfig[card?.gift_card_details?.retailer] || { logo: "/images/amazon.png", bg: "#000000" };
            return (
              <CardTile
                key={index}
                bg={bg}
                logo={logo}
                infoSlot={
                  <div style={purchasedInfoStyle}>
                    <div style={infoTextStyle}>
                      {card?.status === "READY_FOR_REDEMPTION"
                        ? `PURCHASED: ${card?.created_at}`
                        : card?.status === "REJECTED"
                        ? "REJECTED"
                        : "PROCESSING"}
                    </div>
                  </div>
                }
                typeLabel={cardTypeLabel(card?.gift_card_details?.gift_card_type)}
                companyLabel={retailerLabel(card?.gift_card_details?.retailer)}
                priceLabel={`$ ${card?.actual_amount}`}
                onClick={() => handleStoreCardOpen(card)}
              />
            );
          })}
      </div>

      {/* Gift Cards Store section */}
      <div style={{ marginTop: 40, marginBottom: 10 }}>
        <h2 style={{ fontSize: "1.4rem", fontWeight: 700, margin: "0 0 4px 0" }}>Gift Cards Store</h2>
        <p style={{ color: "rgba(255,255,255,0.52)", fontFamily: "Inter", fontWeight: 300, fontSize: 15, margin: 0 }}>
          Find more exciting gift cards and offers below.
        </p>
      </div>
      <br />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
        {Object.entries(transformedData)?.map(
          ([retailer, { gift_cards, min_amount, max_amount, max_boost, gift_card_type }], index) => {
            const { logo, bg } = companyConfig[retailer] || { logo: "/images/amazon.png", bg: "#000000" };
            return (
              <CardTile
                key={index}
                bg={bg}
                logo={logo}
                infoSlot={
                  <div style={infoLayoutStyle(bg)}>
                    <div style={infoTextStyle}>{max_boost}% BOOST</div>
                  </div>
                }
                typeLabel={cardTypeLabel(gift_card_type)}
                companyLabel={retailerLabel(retailer)}
                priceLabel={min_amount === max_amount ? `$ ${min_amount}` : `$ ${min_amount} - ${max_amount}`}
                onClick={() =>
                  navigate("/dashboard/wallet/gift-cards/my-gift-card", {
                    state: { card: [retailer, { gift_cards, min_amount, max_amount, max_boost, gift_card_type }] },
                  })
                }
              />
            );
          }
        )}
      </div>

      {/* ── Dialog: Store Card (Ready for Redemption) ─────────── */}
      <Dialog open={openStoreCard} onOpenChange={setOpenStoreCard}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCard?.gift_card_details?.retailer?.replace(/_/g, " ")}</DialogTitle>
          </DialogHeader>
          <DialogCardPreview
            card={selectedCard}
            showBarcodeSection
            infoSlot={
              <div style={purchasedInfoStyle}>
                <div style={infoTextStyle}>
                  {selectedCard?.status === "READY_FOR_REDEMPTION"
                    ? `PURCHASED: ${selectedCard?.created_at}`
                    : "PROCESSING"}
                </div>
              </div>
            }
          />
          <div style={{ display: "flex", gap: 12, marginTop: 16, justifyContent: "space-between" }}>
            <Button
              variant="outline"
              style={{ flex: 1 }}
              onClick={() => {
                if (selectedCard?.gift_card_code) {
                  navigator.clipboard.writeText(selectedCard.gift_card_code)
                    .then(() => toast("Gift card code copied to clipboard!", { type: "success", autoClose: 2000 }))
                    .catch(() => toast("Failed to copy code", { type: "error" }));
                } else {
                  toast("No gift card code available", { type: "warning" });
                }
              }}
            >
              Copy Code
            </Button>
            <Button
              variant="outline"
              style={{ flex: 1 }}
              disabled={!imageBase64}
              onClick={() => {
                try {
                  const base64Data = imageBase64.includes("data:") ? imageBase64.split(",")[1] : imageBase64;
                  downloadDocument(base64Data, `${selectedCard?.gift_card_code || "barcode"}.png`);
                } catch {
                  toast("Unable to download barcode image", { type: "error" });
                }
              }}
            >
              Download Barcode
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Card Processing ───────────────────────────── */}
      <Dialog open={openCardProcessing} onOpenChange={setOpenCardProcessing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCard?.gift_card_details?.retailer?.replace(/_/g, " ")}</DialogTitle>
          </DialogHeader>
          <div style={{ border: "1px solid #3C3C3C", borderRadius: 10, padding: 8 }}>
            <div style={{ background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)", backgroundColor: selectedCard?.bg, borderRadius: 4 }}>
              <div style={{ textAlign: "center", paddingTop: 30, height: 100 }}>
                <img src={selectedCard?.logo} alt={selectedCard?.retailer} style={{ height: 32 }} />
              </div>
              <div style={{ height: 80, textAlign: "center", padding: "8px 16px" }}>
                <span style={{ fontSize: "1.1rem", letterSpacing: 1 }}>
                  We are processing your request. It might take upto 72hrs to deliver your card details.
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", padding: "0 30px", height: 84 }}>
                <span style={{ fontWeight: 400, fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>
                  {cardTypeLabel(selectedCard?.gift_card_details?.gift_card_type)}
                </span>
                <span style={{ fontSize: "1.1rem", letterSpacing: 2 }}>${selectedCard?.discounted_amount}</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Rejected Card ─────────────────────────────── */}
      <Dialog open={openRejectedCard} onOpenChange={setOpenRejectedCard}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCard?.gift_card_details?.retailer?.replace(/_/g, " ")}</DialogTitle>
          </DialogHeader>
          <div style={{ border: "1px solid #3C3C3C", borderRadius: 10, padding: 8 }}>
            <div style={{ background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)", backgroundColor: selectedCard?.bg, borderRadius: 4 }}>
              <div style={{ textAlign: "center", paddingTop: 30, height: 100 }}>
                <img src={selectedCard?.logo} alt={selectedCard?.retailer} style={{ height: 32 }} />
              </div>
              <div style={{ height: 80, textAlign: "center", padding: "8px 16px" }}>
                <p style={{ fontSize: "1rem", letterSpacing: 1, margin: "0 0 4px 0" }}>
                  The gift card request has been rejected
                </p>
                {selectedCard?.notes && (
                  <p style={{ fontSize: "0.85rem", letterSpacing: 1, margin: 0 }}>
                    Reason: {selectedCard?.notes}
                  </p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Order Success ─────────────────────────────── */}
      <Dialog open={openOrderSuccess} onOpenChange={setOpenOrderSuccess}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCard?.company}</DialogTitle>
          </DialogHeader>
          <div style={{ textAlign: "center", fontFamily: "Arial", padding: "20px 10px" }}>
            <div style={{ marginBottom: 16 }}>
              <Check
                style={{
                  color: "#00838F",
                  backgroundColor: "#00838f3b",
                  fontSize: 50,
                  borderRadius: "50%",
                  padding: 5,
                  width: 50,
                  height: 50,
                }}
              />
            </div>
            <div style={{ height: 110, padding: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Order Placed Successfully</div>
              <div style={{ fontWeight: 400, fontSize: 16, color: "#FFFFFF99", lineHeight: "26px" }}>
                It may take up to 72 hrs for you to deliver the card details. We will also inform via your registered mail address. Happy shopping!
              </div>
            </div>
            <Button onClick={() => setOpenOrderSuccess(false)}>Go to My Gift Cards</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftCards;
